import he from 'he';
import { inchesToMm } from 'utils/number';
import exportToSvg from 'utils/svg-export';
import { find, first, merge, get, flatten } from 'lodash';
import { changeLayoutId, updateConfig } from 'actions/scene';
import store from 'store';
import { close } from 'actions/modal';
import { layoutModes, defaultPageCoordinates, modalTypes, modes } from 'constants/index';
import { defaultColors } from 'api/colors';
import { mmToPx, openModal, storeGetState } from 'utils/utils';
import StoreSceneHelper from '../utils/SceneHelper';
import { layouts, fullScreenLayout } from './layouts';

const JPG_EXPORT_DPI = 300;
const JPG_EXPORT_QUALITY = 1;
const xLinkNamespace = 'http://www.w3.org/1999/xlink';
const googleFontsApiUrl = 'https://fonts.googleapis.com/css?family=';

const selectLayouts = (enableMultiLayout, layoutMode, mode) => {
  const filteredLayouts = layouts.filter((layout) => {
    if (layout.modes) {
      return layout.modes.includes(mode);
    }
    return true;
  });
  if (layoutMode) {
    if (layoutMode === layoutModes.singleOnly) {
      return fullScreenLayout;
    }
    if (layoutMode === layoutModes.multiOnly) {
      return filteredLayouts;
    }
    return [...fullScreenLayout, ...filteredLayouts];
  }
  if (enableMultiLayout) {
    return filteredLayouts;
  }
  return fullScreenLayout;
};

const transformPages = (pages) => {
  if (pages) {
    return pages.map((page) => {
      const _dimensions = page?.config?.dimensions;
      const dimensions = _dimensions && {
        ..._dimensions,
        width: inchesToMm(_dimensions.width),
        height: inchesToMm(_dimensions.height),
      };
      return merge({}, page, { config: { dimensions } });
    });
  }
  return null;
};

const getBackgroundConfig = (value, _backgroundOptions) => {
  /** @type {ColorPreset[]} */
  const backgroundOptions = _backgroundOptions || defaultColors;
  /** @type {ColorPreset} */
  let currentBackground = null;
  if (currentBackground) {
    currentBackground = find(backgroundOptions, { default: true }) || { value };
  }
  if (!currentBackground) currentBackground = find(backgroundOptions, { default: true }) || first(backgroundOptions);
  return { backgroundOptions, currentBackground };
};

export const processSizes = (sizes, enableMultiLayout, layoutMode, defaultBackground, backgroundOptions, mode) =>
  sizes.map((size) => {
    let height = inchesToMm(size.value.dimensions.height);
    let width = inchesToMm(size.value.dimensions.width);
    const pages = transformPages(size.value?.pages);
    const pageCoordinates = get(size, 'value.pageCoordinates') || defaultPageCoordinates;
    let { xLabel, yLabel } = size.value;
    const widthLessThanHeight = width < height; // width is always wider than height
    if (widthLessThanHeight) {
      [width, height] = [height, width];
      [xLabel, yLabel] = [yLabel, xLabel];
    }
    return {
      ...size,
      description: he.decode(size.description),
      value: {
        ...size.value,
        dimensions: {
          width,
          height,
        },
        pages,
        pageCoordinates,
        layouts: selectLayouts(enableMultiLayout, layoutMode, mode),
        xLabel,
        yLabel,
        overlay3dUrl: size.value.overlay3dUrl || '',
        scaleOverlay: size.value.scaleOverlay || 1,
        scaleOverlay3d: size.value.scaleOverlay3d || 1,
        ...getBackgroundConfig(defaultBackground, backgroundOptions),
      },
    };
  });

const downloadS = (scene, download, options) => {
  const svgString = exportToSvg(scene, options);
  if (download === 'string') {
    return svgString;
  }

  const blob = new Blob([svgString], { type: 'image/svg+xml' });
  const url = (window.URL || window.webkitURL).createObjectURL(blob);

  if (download) {
    const a = document.createElement('a');
    a.download = `${new Date().getTime()}.svg`;
    a.type = 'image/svg+xml';
    a.href = url;
    a.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, view: window }));
  } else {
    window.open(url, '_blank');
  }
  return null;
};

const downloadBlob = (blob, fileName, mimeType) => {
  const URLApi = window.URL || window.webkitURL;
  const url = URLApi.createObjectURL(blob);
  const a = document.createElement('a');
  a.download = fileName;
  a.type = mimeType;
  a.href = url;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URLApi.revokeObjectURL(url), 0);
};

const blobToDataUrl = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error || new Error('Failed to read blob as data URL.'));
    reader.readAsDataURL(blob);
  });

const loadImage = (url) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('Failed to load SVG export into image.'));
    image.src = url;
  });

const canvasToBlob = (canvas, mimeType, quality) =>
  new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
          return;
        }
        reject(new Error('Failed to create JPG blob from canvas.'));
      },
      mimeType,
      quality,
    );
  });

const getGoogleFontsCssUrl = (fontFamilies = []) => {
  if (!fontFamilies.length) {
    return null;
  }

  const query = fontFamilies.map((fontFamily) => fontFamily.trim().replace(/\s+/g, '+')).join('|');
  return `${googleFontsApiUrl}${query}&display=swap`;
};

const waitForDocumentFonts = async (fontFamilies = []) => {
  if (!fontFamilies.length || !document.fonts?.load) {
    return;
  }

  await Promise.all(
    fontFamilies.flatMap((fontFamily) => [
      document.fonts.load(`400 16px "${fontFamily}"`),
      document.fonts.load(`700 16px "${fontFamily}"`),
      document.fonts.load(`italic 400 16px "${fontFamily}"`),
      document.fonts.load(`italic 700 16px "${fontFamily}"`),
    ]),
  );

  if (document.fonts?.ready) {
    await document.fonts.ready;
  }
};

const inlineCssAssetUrls = async (cssText) => {
  const urlMatches = [...cssText.matchAll(/url\((['"]?)(https?:\/\/[^)"']+)\1\)/g)];

  if (!urlMatches.length) {
    return cssText;
  }

  const dataUrlBySource = new Map();

  await Promise.all(
    urlMatches.map(async ([, , sourceUrl]) => {
      if (dataUrlBySource.has(sourceUrl)) {
        return;
      }

      try {
        const response = await fetch(sourceUrl);
        if (!response.ok) {
          return;
        }

        dataUrlBySource.set(sourceUrl, await blobToDataUrl(await response.blob()));
      } catch (error) {
        console.warn('[DEV] Failed to inline font file for JPG export.', error);
      }
    }),
  );

  let updatedCssText = cssText;
  dataUrlBySource.forEach((dataUrl, sourceUrl) => {
    updatedCssText = updatedCssText.split(sourceUrl).join(dataUrl);
  });

  return updatedCssText;
};

const buildEmbeddedFontCss = async (fontFamilies = []) => {
  const cssUrl = getGoogleFontsCssUrl(fontFamilies);
  if (!cssUrl) {
    return '';
  }

  try {
    const response = await fetch(cssUrl);
    if (!response.ok) {
      return '';
    }

    return inlineCssAssetUrls(await response.text());
  } catch (error) {
    console.warn('[DEV] Failed to fetch Google Fonts CSS for JPG export.', error);
    return '';
  }
};

const parseSvgDimensions = (svgString) => {
  const svgDocument = new window.DOMParser().parseFromString(svgString, 'image/svg+xml');
  const svgElement = svgDocument.documentElement;
  const width = Number.parseFloat(svgElement.getAttribute('width'));
  const height = Number.parseFloat(svgElement.getAttribute('height'));
  if (Number.isFinite(width) && Number.isFinite(height)) {
    return { width, height };
  }

  const viewBoxValues = (svgElement.getAttribute('viewBox') || '')
    .trim()
    .split(/\s+/)
    .map((value) => Number.parseFloat(value));

  if (viewBoxValues.length === 4 && viewBoxValues.every((value) => Number.isFinite(value))) {
    return { width: viewBoxValues[2], height: viewBoxValues[3] };
  }

  return { width: null, height: null };
};

const inlineSvgAssets = async (svgString) => {
  const svgDocument = new window.DOMParser().parseFromString(svgString, 'image/svg+xml');
  const svgElement = svgDocument.documentElement;
  const imageElements = Array.from(svgElement.querySelectorAll('image'));

  await Promise.all(
    imageElements.map(async (imageElement) => {
      const href = imageElement.getAttribute('href') || imageElement.getAttributeNS(xLinkNamespace, 'href');
      if (!href || href.startsWith('data:') || href.startsWith('blob:')) {
        return;
      }

      try {
        const response = await fetch(href);
        if (!response.ok) {
          return;
        }
        const dataUrl = await blobToDataUrl(await response.blob());
        imageElement.setAttribute('href', dataUrl);
        imageElement.setAttributeNS(xLinkNamespace, 'href', dataUrl);
      } catch (error) {
        console.warn('[DEV] Failed to inline SVG asset for JPG export.', error);
      }
    }),
  );

  return new window.XMLSerializer().serializeToString(svgElement);
};

const embedFontCssIntoSvg = (svgString, fontCss) => {
  if (!fontCss) {
    return svgString;
  }

  const svgDocument = new window.DOMParser().parseFromString(svgString, 'image/svg+xml');
  const svgElement = svgDocument.documentElement;
  const defsElement = svgElement.querySelector('defs') || svgDocument.createElementNS(svgElement.namespaceURI, 'defs');

  if (!defsElement.parentNode) {
    svgElement.insertBefore(defsElement, svgElement.firstChild);
  }

  Array.from(defsElement.querySelectorAll('style')).forEach((styleElement) => {
    if (styleElement.textContent?.includes('fonts.googleapis.com')) {
      styleElement.remove();
    }
  });

  const styleElement = svgDocument.createElementNS(svgElement.namespaceURI, 'style');
  styleElement.setAttribute('type', 'text/css');
  styleElement.textContent = fontCss;
  defsElement.insertBefore(styleElement, defsElement.firstChild);

  return new window.XMLSerializer().serializeToString(svgElement);
};

const createJpgBlobFromSvg = async (svgString, fontFamilies = [], exportDimensionsMm = {}) => {
  await waitForDocumentFonts(fontFamilies);
  const [svgWithInlinedAssets, embeddedFontCss] = await Promise.all([
    inlineSvgAssets(svgString),
    buildEmbeddedFontCss(fontFamilies),
  ]);
  const svgWithEmbeddedFonts = embedFontCssIntoSvg(svgWithInlinedAssets, embeddedFontCss);
  const blob = new Blob([svgWithEmbeddedFonts], { type: 'image/svg+xml;charset=utf-8' });
  const url = (window.URL || window.webkitURL).createObjectURL(blob);

  try {
    const image = await loadImage(url);
    const { width: parsedWidthMm, height: parsedHeightMm } = parseSvgDimensions(svgWithEmbeddedFonts);
    const widthMm = exportDimensionsMm.width || parsedWidthMm;
    const heightMm = exportDimensionsMm.height || parsedHeightMm;
    const canvas = document.createElement('canvas');
    const minimumWidthPx = widthMm ? Math.round(mmToPx(widthMm, JPG_EXPORT_DPI)) : 0;
    const minimumHeightPx = heightMm ? Math.round(mmToPx(heightMm, JPG_EXPORT_DPI)) : 0;

    canvas.width = Math.max(1, minimumWidthPx, image.naturalWidth);
    canvas.height = Math.max(1, minimumHeightPx, image.naturalHeight);
    const context = canvas.getContext('2d');

    if (!context) {
      throw new Error('Canvas 2D context is unavailable.');
    }

    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.drawImage(image, 0, 0, canvas.width, canvas.height);

    return canvasToBlob(canvas, 'image/jpeg', JPG_EXPORT_QUALITY);
  } finally {
    (window.URL || window.webkitURL).revokeObjectURL(url);
  }
};

const getPageExports = (scene, pageId, options) => {
  const { renderMask = false } = options || {};
  const pageCoordinates = StoreSceneHelper.getPageCoordinates({ scene }, pageId);
  const fontFamilies = StoreSceneHelper.getFontsByPageId({ scene }, pageId);
  const svgSize = StoreSceneHelper.getSizeSVG({ scene }, pageId);

  return pageCoordinates.map((coordinates, index) => ({
    fileName: `${new Date().getTime()}-${pageId}-${index + 1}.jpg`,
    fontFamilies,
    exportDimensionsMm: {
      width: (svgSize.width * coordinates.width) / 100,
      height: (svgSize.height * coordinates.height) / 100,
    },
    svgString: exportToSvg(scene, { renderMask, coordinates, pageId }),
  }));
};

export const downloadSvg = async (scene, download, options) => {
  const { renderMask = false, onlyCurrentPage = false } = options || {};
  if (onlyCurrentPage) {
    const pageId = StoreSceneHelper.currentPageId({ scene });
    const pageCoordinates = StoreSceneHelper.getPageCoordinates({ scene }, pageId);
    return pageCoordinates.map((coordinates) => downloadS(scene, download, { renderMask, coordinates, pageId }));
  }
  const { pages } = scene;
  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  for (const page of pages) {
    const pageCoordinates = StoreSceneHelper.getPageCoordinates({ scene }, page.id);
    for (const coordinates of pageCoordinates) {
      downloadS(scene, download, { renderMask, pageId: page.id, coordinates });
      // eslint-disable-next-line no-await-in-loop
      await sleep(300); // або більше, залежно від браузера
    }
  }
  return flatten(
    pages.map((page) => {
      const pageCoordinates = StoreSceneHelper.getPageCoordinates({ scene }, page.id);
      return pageCoordinates.map((coordinates) =>
        downloadS(scene, download, { renderMask, pageId: page.id, coordinates }),
      );
    }),
  );
};

export const downloadJpg = async (scene, options) => {
  const { onlyCurrentPage = false } = options || {};
  const exports = onlyCurrentPage
    ? getPageExports(scene, StoreSceneHelper.currentPageId({ scene }), options)
    : flatten(scene.pages.map((page) => getPageExports(scene, page.id, options)));

  for (const currentExport of exports) {
    // eslint-disable-next-line no-await-in-loop
    const jpgBlob = await createJpgBlobFromSvg(
      currentExport.svgString,
      currentExport.fontFamilies,
      currentExport.exportDimensionsMm,
    );
    downloadBlob(jpgBlob, currentExport.fileName, 'image/jpeg');
  }

  return null;
};

/**
 * set layout to scene
 * @param {string} layoutId - layout id
 * @param {LayoutType[]} layouts - layouts
 * @param {string} mode - layouts
 */
export const setLayoutId = (layoutId, layouts, mode) => {
  const currentLayout = find(layouts, { id: layoutId });
  if (currentLayout) {
    const countLayoutApertures = currentLayout.layout.length;
    if (countLayoutApertures === 1 && mode === modes.canvas) {
      const changeLayoutIdContainer =
        ({ withFrame }) =>
        () => {
          store.dispatch(changeLayoutId({ id: currentLayout.id, withFrame }));
          store.dispatch(close());
        };
      openModal({
        type: modalTypes.confirm,
        modalProps: {
          onConfirm: changeLayoutIdContainer({ withFrame: false }),
          onClose: changeLayoutIdContainer({ withFrame: true }),
          header: 'Add layout',
          text: 'Please select where to place the layout',
          confirmText: 'Colour edges',
          cancelText: 'Image on edges',
        },
      });
    } else {
      store.dispatch(changeLayoutId({ id: currentLayout.id, withFrame: false }));
    }
  } else console.warn('unknown size option with id ', layoutId);
};

export const smartSetLayoutId = (layoutId) => {
  const {
    scene: {
      config: { layouts, mode },
    },
  } = storeGetState();
  setLayoutId(layoutId, layouts, mode);
};

export const setCurrentBackground = (value) => {
  const {
    scene: {
      config: { backgroundOptions },
    },
  } = storeGetState();
  const newValues = find(backgroundOptions, { value });
  newValues && store.dispatch(updateConfig({ currentBackground: newValues }));
};
